import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const ORIGIN = "https://fridaystumps.com";
const OG_IMAGE = `${ORIGIN}/images/stump-before.jpg`;

export const BUSINESS_LD = {
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  "@id": `${ORIGIN}/#business`,
  name: "FridayStumps",
  url: `${ORIGIN}/`,
  telephone: "+1-614-325-1273",
  email: "eric@fridaystumps.com",
  image: OG_IMAGE,
  logo: `${ORIGIN}/brand/fridaystumps-logo-square.png`,
  priceRange: "$125+",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Columbus",
    addressRegion: "OH",
    addressCountry: "US",
  },
  areaServed: [
    "Columbus OH", "Worthington OH", "Dublin OH", "Upper Arlington OH", "Clintonville OH",
    "Grandview OH", "Bexley OH", "Hilliard OH", "Westerville OH", "Gahanna OH",
    "Grove City OH", "Powell OH", "Pickerington OH", "Reynoldsburg OH",
  ].map((name) => ({ "@type": "City", name })),
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: "Friday",
    opens: "08:00",
    closes: "18:00",
  },
};

const STUMP_SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Stump Grinding",
  provider: { "@id": `${ORIGIN}/#business` },
  areaServed: BUSINESS_LD.areaServed,
  description:
    "Contactless stump grinding in Columbus, OH every Friday. Text a photo of the stump, get a firm flat quote, and it's ground below grade by Friday.",
  offers: { "@type": "Offer", priceSpecification: { "@type": "PriceSpecification", priceCurrency: "USD", minPrice: 125 } },
};

const CLEARING_SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Forestry Mulching & Land Clearing",
  provider: { "@id": `${ORIGIN}/#business` },
  areaServed: BUSINESS_LD.areaServed,
  description:
    "Forestry mulching and land clearing in the Columbus, OH area: brush clearing, honeysuckle and invasive growth removal, fence line clearing, lot clearing, and trail cutting. Priced by the area after reviewing photos.",
};

const DEFAULT_DESC =
  "Stump grinding & forestry mulching in Columbus, OH. Text a photo, get a firm flat price, it's done Friday — 100% contactless. From $125/stump.";

const PAGES = {
  "/": {
    title: "FridayStumps.com — Stump Grinding, Every Friday | Columbus, OH",
    description: DEFAULT_DESC,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        BUSINESS_LD,
        { ...STUMP_SERVICE_LD, "@context": undefined },
        { ...CLEARING_SERVICE_LD, "@context": undefined },
      ],
    },
  },
  "/stump-grinding": {
    title: "Stump Grinding in Columbus, OH — From $125/Stump | FridayStumps",
    description:
      "Stump grinding every Friday across Columbus and the 614. Text a photo, get a firm flat quote from $125, and it's flat ground by Friday. 100% contactless.",
    jsonLd: STUMP_SERVICE_LD,
  },
  "/land-clearing": {
    title: "Forestry Mulching & Land Clearing — Columbus, OH | FridayStumps",
    description:
      "Forestry mulching, brush clearing, honeysuckle removal, fence line and lot clearing across Columbus, OH. Priced by the area. Founding-customer pricing now.",
    jsonLd: CLEARING_SERVICE_LD,
  },
};

const NOINDEX_PREFIXES = ["/inbox", "/q/", "/accept/", "/payment/"];

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = PAGES[pathname];
    const noindex = NOINDEX_PREFIXES.some((p) => pathname.startsWith(p));

    document.title = page ? page.title : "FridayStumps.com";
    setMeta("name", "description", page ? page.description : DEFAULT_DESC);

    let robots = document.head.querySelector('meta[name="robots"]');
    if (noindex && !robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    if (robots) robots.setAttribute("content", noindex ? "noindex, nofollow" : "index, follow");

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${ORIGIN}${pathname === "/" ? "/" : pathname}`);

    setMeta("property", "og:title", page ? page.title : "FridayStumps.com");
    setMeta("property", "og:description", page ? page.description : DEFAULT_DESC);
    setMeta("property", "og:url", `${ORIGIN}${pathname === "/" ? "/" : pathname}`);
    setMeta("property", "og:image", OG_IMAGE);

    let ld = document.head.querySelector('script[data-seo="jsonld"]');
    if (page?.jsonLd) {
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.setAttribute("data-seo", "jsonld");
        document.head.appendChild(ld);
      }
      const graph = { ...page.jsonLd };
      if (graph["@graph"]) graph["@graph"] = graph["@graph"].map((n) => ({ ...n }));
      ld.textContent = JSON.stringify(graph);
    } else if (ld) {
      ld.remove();
    }
  }, [pathname]);

  return null;
}
